import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
  Paper,
  InputAdornment,
  Input,
  Tooltip,
  FormHelperText
} from '@mui/material';
import { Close, AttachFile, Delete, Email } from '@mui/icons-material';
import palette from 'src/theme/palette';
import { toast } from 'react-toastify';
import { get_valuation_email } from 'src/ApiServices';
import { respond_valuation } from 'src/ApiServices';
import useMediaQuery from '@mui/material/useMediaQuery';

// Rich Text Editor imports
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const VToolReplyModal = ({ open, onClose, valuation, onSubmit, loading }) => {
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState('');
  const [editorErrors, setEditorErrors] = useState('');
  const [emailTemplate, setEmailTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');
  // console.log(`valustion yeh lo ` + valuation?.id);
  // console.log(`email ke template ke id yeh loooo ` + emailTemplate?.id);
  // Rich Text Editor modules configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  };

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'script',
    'list',
    'bullet',
    'indent',
    'align',
    'blockquote',
    'code-block',
    'link',
    'image',
    'video'
  ];

  // Fetch email template when modal opens and valuation is available
  useEffect(() => {
    const fetchEmailTemplate = async () => {
      if (open && valuation) {
        try {
          setTemplateLoading(true);
          const response = await get_valuation_email();

          if (response.data) {
            setEmailTemplate(response.data);

            // Parse and pre-fill the message with template content
            const templateBody = response.data.body;
            if (templateBody) {
              try {
                // Parse the JSON string to get the content array
                const parsedBody = JSON.parse(templateBody);

                if (Array.isArray(parsedBody) && parsedBody.length > 0) {
                  // Extract the HTML content from the first item
                  const htmlContent = parsedBody[0]?.content || '';

                  // Replace placeholders with actual valuation data
                  let processedContent = htmlContent;

                  // Replace %NAME% with valuation name
                  if (valuation?.name) {
                    processedContent = processedContent.replace(/%NAME%/g, valuation?.name);
                  }

                  // Replace %ADDRESS% with valuation address
                  if (valuation?.address) {
                    processedContent = processedContent.replace(/%ADDRESS%/g, valuation?.address);
                  }

                  // Replace %PRICE% with estimated value (you can customize this as needed)
                  // For now, we'll use a placeholder text
                  // processedContent = processedContent.replace(/%PRICE%/g, 'To be determined based on market analysis');

                  // You can add more placeholder replacements here as needed
                  // For example: %EMAIL%, %PHONE%, %PROPERTY_TYPE%, etc.

                  // Set the processed content to the message state
                  setMessage(processedContent);
                }
              } catch (parseError) {
                console.error('Error parsing template body:', parseError);
                // Fallback: use the raw body if JSON parsing fails
                setMessage(response.data.body);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching email template:', error);
          // Set a default message if template fetch fails
          // setMessage(valuation ?
          //   `<p>Hi <strong>${valuation.name || 'Customer'},</strong></p>
          //   <p><br></p>
          //   <p>Thank you for submitting your property valuation request for:</p>
          //   <p><strong>${valuation.address || 'the provided address'}</strong></p>
          //   <p><br></p>
          //   <p>Our team will review your request and get back to you shortly with an estimated valuation.</p>
          //   <p><br></p>
          //   <p>Best regards,</p>
          //   <p>Valuation Team</p>` :
          //   ''
          // );
        } finally {
          setTemplateLoading(false);
        }
      }
    };

    fetchEmailTemplate();
  }, [open, valuation]);

  // Reset message when modal closes or valuation changes
  useEffect(() => {
    if (!open) {
      setMessage('');
      setEmailTemplate(null);
    }
  }, [open]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) setFile(selectedFile);
  };

  const handleRemoveFile = () => setFile(null);

  // Function to validate message content
  const validateMessage = (value) => {
    // For rich text, we need to check if there's actual content (not just HTML tags)
    const strippedValue = value?.replace(/<[^>]*>/g, '').trim();
    return strippedValue ? '' : 'Message is required';
  };

  // Handle message change
  const handleMessageChange = (value) => {
    setMessage(value);

    // Validate the message
    const error = validateMessage(value);
    setEditorErrors(error);
  };

  const handleSubmit = async () => {
    const error = validateMessage(message);
    if (error) return setEditorErrors(error);

    if (!valuation?.email) return;

    try {
      const formData = new FormData();
      formData.append('id', emailTemplate?.id);
      formData.append('email', valuation?.email);
      formData.append('message', message);

      if (file) {
        const blob = new Blob([file], { type: file.type });
        formData.append('attachment', blob, file.name);
      }

      await respond_valuation(formData);

      setMessage('');
      setFile(null);
      onClose();

      if (onSubmit) onSubmit();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong. Failed to send reply!');
    }
  };

  if (!valuation) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          margin: { xs: 2, sm: 3 },
          maxHeight: isMobile ? '100vh' : '90vh',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          width: isMobile ? '100vw' : '1400px',
          margin: isMobile ? 0 : undefined
        }
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`,
          color: palette.primary.contrastText,
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography sx={{ fontSize: 20, fontFamily: 'poppinsSemiBold' }}>
          Reply to Valuation Inquiry
        </Typography>
        <IconButton onClick={onClose} sx={{ color: palette.primary.contrastText }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          overflowX: 'hidden',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontFamily: 'poppinsSemiBold', mb: 2 }}>
            Send Reply
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontFamily: 'poppinsMedium', mb: 1 }}>
              To Email
            </Typography>
            <TextField
              fullWidth
              value={valuation?.email || ''}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: palette.text.secondary }} />
                  </InputAdornment>
                )
              }}
              variant="outlined"
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: palette.grey[50],
                  '& fieldset': { borderColor: palette.grey[300] }
                }
              }}
            />
          </Box>

          {/* Rich Text Editor for Message */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'poppinsMedium' }}>
                Message *
              </Typography>

              {templateLoading ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: palette.info.main,
                    fontFamily: 'poppinsRegular',
                    fontStyle: 'italic'
                  }}
                >
                  Loading email template...
                </Typography>
              ) : emailTemplate ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: palette.success.main,
                    fontFamily: 'poppinsRegular'
                  }}
                >
                  Using template: {emailTemplate.name}
                </Typography>
              ) : null}
            </Box>

            <Box
              sx={{
                border: editorErrors
                  ? `1px solid ${palette.error.main}`
                  : `1px solid ${palette.primary.inputborder}`,
                borderRadius: 1,
                width: '100%',
                overflow: 'hidden',
                '& .ql-toolbar': {
                  border: 'none',
                  borderBottom: `1px solid ${palette.primary.inputborder}`,
                  backgroundColor: palette.background.neutral,
                  '& .ql-picker-label': {
                    fontFamily: 'poppinsRegular'
                  },
                  '& .ql-picker-options': {
                    fontFamily: 'poppinsRegular'
                  }
                },
                '& .ql-container': {
                  border: 'none',
                  fontFamily: 'poppinsRegular',
                  fontSize: '14px',
                  minHeight: '250px'
                },
                '& .ql-editor': {
                  minHeight: '250px',
                  fontFamily: 'poppinsRegular',
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    fontFamily: 'poppinsSemiBold',
                    marginTop: '8px',
                    marginBottom: '8px'
                  },
                  '& a': {
                    color: palette.primary.main,
                    textDecoration: 'underline',
                    '&:hover': {
                      color: palette.primary.dark
                    }
                  },
                  '& img': {
                    cursor: 'pointer',
                    borderRadius: '4px',
                    transition: 'all 0.3s ease',
                    maxWidth: '100%',
                    height: 'auto',
                    margin: '8px 0',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: `0 4px 12px ${palette.primary.main}40`
                    }
                  },
                  '&.ql-blank::before': {
                    color: palette.text.disabled,
                    fontStyle: 'normal',
                    fontFamily: 'poppinsRegular'
                  }
                },
                '&:hover': {
                  borderColor: palette.primary.main
                },
                '&:focus-within': {
                  borderColor: palette.primary.main,
                  borderWidth: 2
                }
              }}
            >
              <ReactQuill
                value={message}
                onChange={handleMessageChange}
                modules={modules}
                formats={formats}
                placeholder="Type your reply message here... You can format text, add links, images, etc."
                theme="snow"
                readOnly={templateLoading}
              />
            </Box>

            {editorErrors && (
              <FormHelperText
                error
                sx={{
                  fontFamily: 'poppinsRegular',
                  ml: 0,
                  mt: 1
                }}
              >
                {editorErrors}
              </FormHelperText>
            )}

            {/* Information about placeholders */}
            {/* {emailTemplate && (
              <Box sx={{ mt: 1 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: palette.info.main,
                    fontFamily: 'poppinsRegular',
                    fontStyle: 'italic'
                  }}
                >
                  Note: Template placeholders (%NAME%, %ADDRESS%, %PRICE%) have been automatically replaced with valuation data.
                </Typography>
              </Box>
            )} */}
          </Box>

          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: 'poppinsMedium', mb: 1 }}>
              Attachment (Optional)
            </Typography>

            {!file ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: `2px dashed ${palette.grey[300]}`,
                  backgroundColor: palette.grey[50],
                  cursor: 'pointer',
                  width: '100%',
                  '&:hover': { borderColor: palette.primary.main }
                }}
                onClick={() => document.getElementById('file-upload').click()}
              >
                <AttachFile sx={{ color: palette.text.secondary, fontSize: 40, mb: 1 }} />
                <Typography variant="body2" sx={{ color: palette.text.secondary, mb: 1 }}>
                  Click to upload file or drag and drop
                </Typography>
                <Typography variant="caption" sx={{ color: palette.text.disabled }}>
                  PDF, DOC, JPG, PNG up to 10MB
                </Typography>
                <Input
                  id="file-upload"
                  type="file"
                  inputProps={{ accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png' }}
                  onChange={handleFileChange}
                  sx={{ display: 'none' }}
                />
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: palette.grey[50] }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AttachFile sx={{ color: palette.primary.main }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontFamily: 'poppinsMedium' }}>
                        {file?.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: palette.text.secondary }}>
                        {(file?.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                  </Box>
                  <Tooltip title="Remove File">
                    <IconButton size="small" onClick={handleRemoveFile}>
                      <Delete sx={{ color: palette.error.main }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          borderTop: `1px solid ${palette.divider}`,
          alignItems: { xs: 'stretch', sm: 'center' }
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{ fontFamily: 'poppinsMedium', px: 3, width: { xs: '100%', sm: 'auto' } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !message.trim()}
          startIcon={<Email />}
          sx={{
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            fontFamily: 'poppinsMedium',
            width: { xs: '100%', sm: 'auto' },
            px: 3,
            '&:hover': { backgroundColor: palette.primary.dark }
          }}
        >
          {loading ? 'Sending...' : 'Send Reply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VToolReplyModal;
